import React from 'react';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import { Link } from 'react-router-dom';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const TeamHealthChecksStage1 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 1: Crowd-source Team Health Indicators" icon={<LightBulbIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Crowd-source a consistent(ish) set of indicators from your teams. These should be the core 10 or so things that impact team health</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Build a miro board template to reflect this that will act as your central hub for future health checks</span>
            </li>
          </ul>
          <p className="mt-4">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Crowd-source what makes a team healthy</h3>
        <p className="text-gray-200 mb-4">
          To run a team health check you need to create a set of 5-10 gauges that represent how your teams want to measure team health.
        </p>
        
        <h4 className="text-lg font-semibold text-white mb-3">The goals here are:</h4>
        <ul className="list-none space-y-2 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Engage all teams in the co-creation</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Try and normalise the gauges across all teams but allow variance for the definitions of awesome / definitions of crappy.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Publish</span>
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mb-3">Action:</h4>
        <ol className="list-decimal pl-6 space-y-3 text-gray-200">
          <li>Create a simple miro board with some examples of team health measures, check out the <Link to="/playbooks/team-health-checks/useful-resources" className="text-primary hover:text-primary-light">Useful Resources Section</Link> for several starter kits / inspo. I'd recommend something like this as a starter:</li>
        </ol>
        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/simple-board.png" alt="Miro board example" />
        
        <div className="mt-4 mb-4">
          <ToolCallout
            tool="miro"
            label="Team Health Check Starter Template"
            href="https://miro.com/app/board/uXjVKNRa3YU=/?share_link_id=643314144358"
            description="A simple template to get started with team health checks"
          />
        </div>
        
        <ol className="list-decimal pl-6 space-y-3 text-gray-200" start="2">
          <li>Invite teams to the board (this can be async or sync depending on how you like to work and can either do one team at a time or my preference: all together), perhaps add a short loom to introduce what this is about and invite them to:
            <ol className="list-decimal pl-6 mt-2 space-y-2">
              <li>dot vote on the board which cards resonate with them the most as useful indicators of team health</li>
              <li>comment their suggestions for improving the "Definition of Awesome" and "Definition of Dumpster Fire"</li>
              <li>suggest any additional team health check cards using the {'{spare card}'}</li>
            </ol>
          </li>
        </ol>

        <ExpandableCallout type="info" title="Facilitation tip:" className="mt-4">
          <p className="text-gray-200">
            This should generate a lot of conversation and also through #co-creation we're engaging all voices. Personally I like to do this either: 100% async or sync but with music on so folks are 'talking with sticky notes and comments' not their voices as it helps include everyone regardless of where you are on the introvert&lt;&gt;extrovert continuum.
          </p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/dot-vote.png" alt="Dot voting and synthesising comments" />
        <p className="text-gray-200 mb-4">Dot voting and synthesising comments into new "Definition of Awesome" and "Definition of Dumpster Fire"</p>
        
        <p className="text-gray-200 mb-4">After facilitating this exercise you should have enough signal from the comments and the dot voting to:</p>
        <ul className="list-none space-y-2 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Prune any redundant health check cards</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Refine popular ones</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Tweak the "Definition of Awesome" and "Definition of Dumpster Fire" for each</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Add in any suggested extra ones.</span>
          </li>
        </ul>

        <ExpandableCallout type="info" title="Tip:" className="mt-4">
          <p className="text-gray-200">Try and keep the set to no more than 10 to avoid this becoming unwieldy later on.</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Action</h3>
        <p className="text-gray-200 mb-4">Publish as a memo or in slack</p>
        
        <CopyToNotion title="Notion Memo Template">
          <div className="space-y-4">
            <p>Hey folks,</p>
            
            <p>Thanks for co-creating what team health looks like at {'{company_name}'}. I've synthesised all the comments and feedbacks and we've landed on the following dials as a way for us to check in together every quarter on team health:</p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li>Value</li>
              <li>Healthy Backlog</li>
              <li>etc</li>
            </ul>
            
            <p>Next steps from me:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>I'll be working with each team to facilitate our first company wide health check</li>
              <li>We'll use the learnings from the sessions to prioritise continuous improvement actions that focus on our ways of working aka our culture.</li>
            </ol>
            
            <p>Thanks,</p>
            <p>- Your name</p>
          </div>
        </CopyToNotion>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <p className="text-gray-200">
          Folks feel included, engaged and excited about running their first team health check.
        </p>
        <p className="text-gray-200 mt-2">
          Next up: supporting the first round of health checks across the org.
        </p>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default TeamHealthChecksStage1; 